/**
 * Helper functions for the syncMessagesFromApi reducer.
 */

import type { ChatMessageEntity, SyncMessagesFromApiPayload } from './chat-message-entity.interface'
import type { ChatState } from './chat-state.interface'

const getPendingMessages = (
  state: ChatState,
  conversationId: string
): (ChatMessageEntity | undefined)[] => {
  const existingIdList = state.messageIdsByConversation[conversationId] ?? []
  return existingIdList.map((id) => state.messageEntities[id]).filter((msg) => msg?.isPending)
}

const clearNonPendingMessages = (state: ChatState, conversationId: string): void => {
  const existingIdList = state.messageIdsByConversation[conversationId] ?? []
  existingIdList.forEach((id) => {
    const msg = state.messageEntities[id]
    if (msg && !msg.isPending) {
      delete state.messageEntities[id]
    }
  })
}

const syncFirstPage = (
  state: ChatState,
  conversationId: string,
  messageList: ChatMessageEntity[]
): void => {
  const pendingMessages = getPendingMessages(state, conversationId)
  clearNonPendingMessages(state, conversationId)

  const newIdList: string[] = []

  messageList.forEach((msg) => {
    if (!state.messageEntities[msg.id]) {
      state.messageEntities[msg.id] = msg
      newIdList.push(msg.id)
    }
  })

  pendingMessages.forEach((msg) => {
    if (msg && !newIdList.includes(msg.id)) {
      newIdList.push(msg.id)
    }
  })

  state.messageIdsByConversation[conversationId] = newIdList
}

const syncSubsequentPage = (
  state: ChatState,
  conversationId: string,
  messageList: ChatMessageEntity[]
): void => {
  const existingIdSet = new Set(state.messageIdsByConversation[conversationId] ?? [])

  messageList.forEach((msg) => {
    if (!existingIdSet.has(msg.id)) {
      state.messageEntities[msg.id] = msg
      state.messageIdsByConversation[conversationId].push(msg.id)
    }
  })
}

export const handleSyncMessagesFromApi = (
  state: ChatState,
  payload: SyncMessagesFromApiPayload
): void => {
  const { conversationId, messageList, hasMore, isFirstPage } = payload

  if (isFirstPage) {
    syncFirstPage(state, conversationId, messageList)
  }

  if (!isFirstPage) {
    syncSubsequentPage(state, conversationId, messageList)
  }

  state.paginationByConversation[conversationId] = {
    page: state.paginationByConversation[conversationId]?.page ?? 1,
    hasMore,
  }
}
